import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Search, ChefHat, Clock, Users, X, Loader2, AlertCircle } from 'lucide-react';
import { useRecipes } from '@/hooks/useRecipes';
import { api } from '@/lib/api';
import { RecipeImage } from '@/components/RecipeImage';

interface Recipe {
    id: string;
    name: string;
    description?: string;
    imageUrl?: string;
    prepTime?: number;
    cookTime?: number;
    servings: number;
    calories?: number;
    difficulty?: string;
    nutritionCategories?: Array<{
        categoryId: string;
        servings: number;
        category: {
            id: string;
            name: string;
            points: number;
        };
    }>;
}

interface Props {
    onSelectRecipe: (recipe: Recipe) => void;
    onCancel?: () => void;
}

export const RecipeSelector: React.FC<Props> = ({ onSelectRecipe, onCancel }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);

    // Fetch recipes with search
    const { data: recipesData, isLoading } = useRecipes({ search: searchTerm });
    const recipes = recipesData?.recipes || [];

    const handleSelectRecipe = (recipe: Recipe) => {
        setSelectedRecipe(recipe);
    };

    const [isConfirming, setIsConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        if (!selectedRecipe) return;

        setIsConfirming(true);
        setError(null);

        // Fetch full recipe details with nutrition categories
        try {
            const response = await api.get(`/recipes/${selectedRecipe.id}`);
            const fullRecipe = response.data;

            // Fetch nutrition categories
            try {
                const nutritionResponse = await api.get(
                    `/recipes/${selectedRecipe.id}/nutrition-categories`,
                );
                fullRecipe.nutritionCategories = nutritionResponse.data;
            } catch (nutritionError) {
                console.warn('No nutrition categories found for recipe, using empty array');
                fullRecipe.nutritionCategories = [];
            }

            onSelectRecipe(fullRecipe);
        } catch (error) {
            console.error('Error fetching recipe details:', error);
            setError('Failed to load recipe details. Please try again.');
        } finally {
            setIsConfirming(false);
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'EASY':
                return 'text-brand-text-green';
            case 'MEDIUM':
                return 'text-foreground';
            case 'HARD':
                return 'text-foreground';
            default:
                return 'text-muted-foreground';
        }
    };

    // One scroll region, the modal's, with the title and search pinned to its
    // top and the confirm action pinned to its bottom. The list used to scroll
    // inside its own max-h-96 box with "Use This Recipe" below it, so on a
    // phone the button sat out of sight after picking a recipe (#107).
    // The -top-6/-bottom-6 offsets and matching padding cover the p-6 of the
    // Card that MealLogForm scrolls this in, so rows do not show through the
    // padding above the title or below the buttons.
    return (
        <div className="space-y-4">
            <div className="sticky -top-6 z-10 -mx-1 -mt-6 space-y-4 bg-card px-1 pt-6 pb-1">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Select a Recipe</h3>
                    {onCancel && (
                        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
                            <X className="w-4 h-4" />
                        </Button>
                    )}
                </div>

                {/* Search Bar */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                        type="text"
                        placeholder="Search recipes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* Recipe List */}
            <div className="space-y-2">
                {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading recipes...</div>
                ) : recipes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        {searchTerm ? 'No recipes found' : 'Start typing to search recipes'}
                    </div>
                ) : (
                    recipes.map((recipe: Recipe) => (
                        <Card
                            key={recipe.id}
                            className={`p-4 cursor-pointer transition-all ${
                                selectedRecipe?.id === recipe.id
                                    ? 'border-brand-header border-2 bg-brand-header-wash'
                                    : 'hover:border-brand-header'
                            }`}
                            onClick={() => handleSelectRecipe(recipe)}
                        >
                            <div className="flex gap-4">
                                {recipe.imageUrl && (
                                    <RecipeImage
                                        src={recipe.imageUrl}
                                        alt={recipe.name}
                                        variant="thumb"
                                        thumbSize={80}
                                        className="w-20 h-20 object-cover rounded-md"
                                    />
                                )}
                                <div className="min-w-0 flex-1">
                                    <h4 className="font-semibold text-foreground">{recipe.name}</h4>
                                    {recipe.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                            {recipe.description}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                                        {recipe.prepTime && (
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {recipe.prepTime + (recipe.cookTime || 0)} min
                                            </span>
                                        )}
                                        {recipe.servings && (
                                            <span className="flex items-center gap-1">
                                                <Users className="w-3 h-3" />
                                                {recipe.servings} servings
                                            </span>
                                        )}
                                        {recipe.difficulty && (
                                            <span
                                                className={`font-medium ${getDifficultyColor(recipe.difficulty)}`}
                                            >
                                                {recipe.difficulty}
                                            </span>
                                        )}
                                        {recipe.calories && <span>{recipe.calories} cal</span>}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Sticky footer: the selection and the confirm action stay in view
                however far down the list the recipe was. */}
            <div className="sticky -bottom-6 z-10 -mx-1 -mb-6 space-y-3 border-t bg-card px-1 pt-4 pb-6">
                {error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                        <p className="text-sm text-destructive">{error}</p>
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-end gap-3">
                    <div className="flex min-w-0 flex-1 basis-40 items-center gap-2">
                        <ChefHat className="w-5 h-5 shrink-0 text-brand-text-blue" />
                        <p className="min-w-0 truncate text-sm text-foreground">
                            {selectedRecipe ? (
                                <>
                                    <span className="text-muted-foreground">Selected: </span>
                                    <span className="font-semibold">{selectedRecipe.name}</span>
                                </>
                            ) : (
                                <span className="text-muted-foreground">
                                    Tap a recipe to select it
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {onCancel && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onCancel}
                                disabled={isConfirming}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={handleConfirm}
                            disabled={!selectedRecipe || isConfirming}
                        >
                            {isConfirming ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading...
                                </>
                            ) : (
                                'Use This Recipe'
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
